import { FC } from 'react';

import useAiSuggestionsQuery from 'api/queries/useAiSuggestionsQuery';

interface AiSuggestionsProps {
  itemId?: string;
}

const AiSuggestionsSkeleton = () => (
  <div className="w-full bg-white rounded-2xl p-8 animate-pulse space-y-6">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-gray-200 rounded-lg" />
      <div className="h-6 bg-gray-200 rounded w-64" />
      <div className="h-6 bg-gray-200 rounded-full w-12" />
    </div>
    <div className="h-4 bg-gray-200 rounded w-72" />
    <div className="space-y-4">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded w-36" />
          </div>
          <div className="h-4 bg-gray-200 rounded w-6" />
        </div>
      ))}
    </div>
  </div>
);

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-4 h-4 text-white"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={3}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const AiSuggestions: FC<AiSuggestionsProps> = ({ itemId }) => {
  const { data, isLoading, isError } = useAiSuggestionsQuery(itemId || '');

  if (!itemId || isError) return null;
  if (isLoading) return <AiSuggestionsSkeleton />;
  if (!data?.complementaryItems?.length) return null;

  return (
    <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 bg-[#22CC7A] rounded-lg flex items-center justify-center flex-shrink-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
        </div>
        <h2 className="font-bold text-lg tracking-wide uppercase">
          Project Prep Checklist
        </h2>
        <span className="bg-[#7C3AED] text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-3 h-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
            />
          </svg>
          AI
        </span>
      </div>

      <p className="text-gray-400 text-sm mb-6 ml-[52px]">
        Before starting your project, gather these items:
      </p>

      {/* Complementary Items */}
      <div className="border border-gray-100 rounded-xl px-5 py-1">
        {data.complementaryItems.map((item, index) => (
          <div key={`item_${index}`}>
            <div className="flex items-start justify-between py-4 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-[#22CC7A] rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckIcon />
                </div>
                <div>
                  <p className="font-medium text-sm text-gray-800">
                    {item.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{item.reason}</p>
                </div>
              </div>
              <span className="text-sm font-medium text-gray-400 flex-shrink-0">
                #{index + 1}
              </span>
            </div>
            {index < data.complementaryItems.length - 1 && (
              <div className="border-b border-dashed border-gray-200" />
            )}
          </div>
        ))}
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-3 mt-6 bg-[#FEF9E7] border-l-4 border-[#F5C842] rounded-r-lg px-4 py-3">
        <div className="w-5 h-5 bg-[#3B82F6] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-white text-xs font-bold">i</span>
        </div>
        <p className="text-sm text-gray-600">
          This is AI-generated guidance only, not a shopping list. Verify what
          you need for your specific project.
        </p>
      </div>
    </div>
  );
};

export default AiSuggestions;
